import { useEffect, useState } from 'react';

/**
 * @hook useSidebarCollapsed
 * @description Is the navigation rail collapsed? One answer, for every shell.
 *
 * The app shell (`core/Layout`) owned this: a `moksy_sidebar_collapsed` key, a
 * reader that defaults to collapsed, and an effect that writes it back. When
 * the idea→report journey gained the same sidebar it needed the same answer,
 * and the obvious move — copy the four lines across — is how the two surfaces
 * start disagreeing: collapse the rail on /projects, walk into a Briefing, and
 * find it expanded, because the second copy never read what the first wrote.
 *
 * It is one preference about one sidebar. So it is held once.
 *
 * Defaults to COLLAPSED: the rail is a 68px strip of icons until somebody asks
 * for more, which is the state the product opens in. `localStorage` is wrapped
 * because a private window throws on access, and a navigation rail that cannot
 * render because it could not remember a preference is worse than one that
 * forgets.
 */
export const SIDEBAR_COLLAPSED_KEY = 'moksy_sidebar_collapsed';

const readInitial = () => {
  if (typeof window === 'undefined') return true;
  try {
    const stored = window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    if (stored === '0') return false;
    if (stored === '1') return true;
  } catch { /* localStorage unavailable */ }
  return true;
};

export const useSidebarCollapsed = () => {
  const [collapsed, setCollapsed] = useState(readInitial);
  useEffect(() => {
    try { window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0'); } catch { /* noop */ }
  }, [collapsed]);
  return [collapsed, setCollapsed];
};

export default useSidebarCollapsed;
